import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { NotFoundView } from '@agency/contracts';
import { Button, EmptyState, Notice, Skeleton, useToast, scrollRegion } from '@agency/ui';
import { ApiClientError, get, post } from '../../lib/api.ts';
import { useAuth } from '../../lib/auth.tsx';
import { fmtDate, relative } from '../../lib/format.ts';
import { QueryError } from '../../lib/query-state.tsx';
import { errText } from '../settings/common.tsx';
import type { RedirectDraft } from './redirect-drawer.tsx';

export function NotFoundTab({ onCreate }: { onCreate: (d: RedirectDraft) => void }) {
  const { can } = useAuth();
  const qc = useQueryClient();
  const toast = useToast();
  const manage = can('seo.manage');
  const q = useQuery<NotFoundView[], ApiClientError>({ queryKey: ['seo', 'not-found'], queryFn: () => get('/admin/seo/not-found') });
  const dismiss = useMutation({
    mutationFn: (path: string) => post('/admin/seo/not-found/dismiss', { path }),
    onSuccess: async (_v, path) => { await qc.invalidateQueries({ queryKey: ['seo'] }); toast({ tone: 'success', message: `Dismissed ${path}.` }); },
  });
  return (
    <>
      <p className="ag-muted" style={{ marginBottom: 12 }}>Addresses visitors requested that do not exist, most requested first (top 100). A suggestion appears when an old address of a live page or a similar live page is found.</p>
      {dismiss.isError ? <div style={{ marginBottom: 12 }}><Notice tone="error" title="Could not dismiss">{errText(dismiss.error)}</Notice></div> : null}
      {q.isLoading ? <Skeleton width="100%" height={220} /> : q.isError ? <QueryError error={q.error} onRetry={() => void q.refetch()} /> : q.data!.length === 0 ? (
        <EmptyState title="No missing pages recorded" icon="check">Nothing has requested an address that does not exist, or every path has been dismissed.</EmptyState>
      ) : (
        <div className="ag-table-wrap" {...scrollRegion('Missing pages')}>
          <table className="ag-table">
            <caption className="ag-visually-hidden">Requested addresses that returned 404</caption>
            <thead><tr><th scope="col">Path</th><th scope="col" className="ag-num">Requests</th><th scope="col">Last seen</th><th scope="col">Last referrer</th><th scope="col">Suggestion</th>{manage ? <th scope="col"><span className="ag-visually-hidden">Actions</span></th> : null}</tr></thead>
            <tbody>
              {q.data!.map((n) => (
                <tr key={n.path}>
                  <td className="ag-mono" style={{ overflowWrap: 'anywhere' }}>{n.path}</td>
                  <td className="ag-num">{n.count}</td>
                  <td><time dateTime={n.lastSeenAt} title={fmtDate(n.lastSeenAt)}>{relative(n.lastSeenAt)}</time></td>
                  <td style={{ overflowWrap: 'anywhere', maxWidth: 240 }}>{n.lastReferrer ?? <span className="ag-muted">Direct or unknown</span>}</td>
                  <td>{n.suggestion ? <span className="ag-mono">{n.suggestion}</span> : <span className="ag-muted">None found</span>}</td>
                  {manage ? (
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <Button size="sm" onClick={() => onCreate({ source: n.path, destination: n.suggestion ?? '', from404: true })} aria-label={`Create redirect for ${n.path}`}>Create redirect</Button>{' '}
                      <Button size="sm" variant="ghost" loading={dismiss.isPending && dismiss.variables === n.path} onClick={() => dismiss.mutate(n.path)} aria-label={`Dismiss ${n.path}`}>Dismiss</Button>
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {!manage ? <p className="ag-muted" style={{ marginTop: 12 }}>You can view missing pages but not create redirects or dismiss them.</p> : null}
    </>
  );
}
